/**
 * Code Search Context
 * Provides shared search state between the feedback panel and code viewer
 * Allows clicking on criteria to automatically search for related code
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface CodeSearchContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  caseSensitive: boolean;
  setCaseSensitive: (value: boolean) => void;
  highlightedCriterion: string | null;
  setHighlightedCriterion: (criterion: string | null) => void;
  questionFilter: number | null; // Filter search to specific question number
  setQuestionFilter: (questionNumber: number | null) => void;
}

const CodeSearchContext = createContext<CodeSearchContextType | undefined>(undefined);

// Export the context itself for components that need to use useContext directly
export { CodeSearchContext };

export const CodeSearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [highlightedCriterion, setHighlightedCriterion] = useState<string | null>(null);
  const [questionFilter, setQuestionFilter] = useState<number | null>(null);

  return (
    <CodeSearchContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        caseSensitive,
        setCaseSensitive,
        highlightedCriterion,
        setHighlightedCriterion,
        questionFilter,
        setQuestionFilter,
      }}
    >
      {children}
    </CodeSearchContext.Provider>
  );
};

export const useCodeSearch = () => {
  const context = useContext(CodeSearchContext);
  if (context === undefined) {
    throw new Error('useCodeSearch must be used within a CodeSearchProvider');
  }
  return context;
};
